import { useEffect, useState } from "react";
import api from "@/lib/api";
import {
  UserMinus, Calendar, IndianRupee, Clock, TrendingDown,
  RotateCcw, LineChart,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";

const fmt = (n) => (typeof n === "number"
  ? n.toLocaleString("en-IN", { maximumFractionDigits: 2 })
  : "—");

const STATUS_STYLES = {
  DRAFT: "bg-amber-100 text-amber-800 border-amber-200",
  APPROVED: "bg-emerald-100 text-emerald-800 border-emerald-200",
};

const COL_ORDER = ["Resignation", "Termination", "Absconding",
  "Retirement", "End of Contract", "Death"];

const COL_ACCENT = {
  Resignation:      "border-t-blue-500",
  Termination:      "border-t-red-500",
  Absconding:       "border-t-orange-500",
  Retirement:       "border-t-emerald-500",
  "End of Contract":"border-t-slate-500",
  Death:            "border-t-purple-500",
};

function AnalyticsBar({ analytics }) {
  if (!analytics) return null;
  const rehire = analytics.rehire_eligibility_split || {};
  const rehireTotal = Object.values(rehire).reduce((a, b) => a + b, 0);
  const pct = (n) => (rehireTotal > 0 ? Math.round(n / rehireTotal * 100) : 0);
  return (
    <div className="grid grid-cols-1 md:grid-cols-4 gap-3" data-testid="exits-analytics-bar">
      <div className="bg-white border border-slate-200 rounded-lg p-4">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
          <TrendingDown className="h-3.5 w-3.5 text-red-600" />
          Attrition (12 months)
        </div>
        <div className="mt-1 text-2xl font-bold text-slate-900" style={{fontFamily:"Chivo"}}>
          {analytics.attrition_rate_annual_pct}%
        </div>
        <div className="text-[11px] text-slate-500 mt-0.5">
          {analytics.exits_last_12_months} exits / {analytics.headcount_current} headcount
        </div>
      </div>
      <div className="bg-white border border-slate-200 rounded-lg p-4">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
          <Clock className="h-3.5 w-3.5 text-blue-600" />
          Avg Notice Served
        </div>
        <div className="mt-1 text-2xl font-bold text-slate-900" style={{fontFamily:"Chivo"}}>
          {analytics.avg_notice_served_days} <span className="text-sm text-slate-500">days</span>
        </div>
        <div className="text-[11px] text-slate-500 mt-0.5">across approved exits (12m)</div>
      </div>
      <div className="bg-white border border-slate-200 rounded-lg p-4 md:col-span-2">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
          <RotateCcw className="h-3.5 w-3.5 text-emerald-600" />
          Rehire Eligibility Split
        </div>
        <div className="flex items-end gap-3 mt-2">
          {["Yes", "No", "Conditional", "Pending"].map((k) => (
            <div key={k} className="flex-1">
              <div className="flex items-baseline gap-1">
                <span className={`text-lg font-bold ${
                  k === "Yes" ? "text-emerald-600" :
                  k === "No" ? "text-red-600" :
                  k === "Conditional" ? "text-amber-600" : "text-slate-500"
                }`}>{rehire[k] || 0}</span>
                <span className="text-[10px] text-slate-500">{pct(rehire[k] || 0)}%</span>
              </div>
              <div className="text-[10px] uppercase tracking-wider text-slate-500">{k}</div>
              <div className="h-1 mt-1 bg-slate-200 rounded-full overflow-hidden">
                <div className={`h-full ${
                  k === "Yes" ? "bg-emerald-500" :
                  k === "No" ? "bg-red-500" :
                  k === "Conditional" ? "bg-amber-500" : "bg-slate-400"
                }`} style={{width: `${pct(rehire[k] || 0)}%`}} />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function ExitCard({ e }) {
  return (
    <div
      className="bg-white border border-slate-200 rounded p-3 hover:shadow-sm transition mb-2"
      data-testid={`exit-card-${e.id}`}
    >
      <div className="flex items-start justify-between gap-2 mb-1">
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-sm text-slate-900 truncate">
            {e.employee_name_at_submit}
          </div>
          <div className="text-[10px] text-slate-500 font-mono">
            {e.punch_id_at_submit} · {e.employee_code_at_submit}
          </div>
        </div>
        <Badge className={`${STATUS_STYLES[e.status] || "bg-slate-100 text-slate-800"} text-[9px]`}>
          {e.status}
        </Badge>
      </div>
      <div className="grid grid-cols-2 gap-1.5 text-[11px] text-slate-700 mt-2">
        <div className="flex items-center gap-1">
          <Calendar className="h-3 w-3 text-slate-400" />
          {e.approved_lwd || e.requested_lwd}
        </div>
        <div className="flex items-center gap-1">
          <Clock className="h-3 w-3 text-slate-400" />
          <span className={e.notice_shortfall_days > 0 ? "text-red-600 font-semibold" : ""}>
            {e.notice_shortfall_days}d short
          </span>
        </div>
        {e.notice_recovery_estimate > 0 && (
          <div className="flex items-center gap-1 col-span-2">
            <IndianRupee className="h-3 w-3 text-slate-400" />
            ₹{fmt(e.notice_recovery_estimate)} recovery
          </div>
        )}
      </div>
      {e.rehire_eligibility && e.rehire_eligibility !== "Pending" && (
        <div className="mt-2 text-[10px] text-slate-500">
          Rehire: <span className={
            e.rehire_eligibility === "Yes" ? "text-emerald-600 font-semibold" :
            e.rehire_eligibility === "No" ? "text-red-600 font-semibold" :
            "text-amber-600 font-semibold"
          }>{e.rehire_eligibility}</span>
        </div>
      )}
    </div>
  );
}

export default function Exits() {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/exits/analytics");
        setAnalytics(data);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const kanban = analytics?.kanban || {};
  const totalCards = Object.values(kanban).reduce((a, arr) => a + arr.length, 0);

  return (
    <div data-testid="exits-page" className="space-y-5">
      <div className="flex items-center gap-3">
        <UserMinus className="h-7 w-7 text-red-600" />
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-widest text-slate-400">Phase B Session 8</div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: "Chivo" }}>
            Employee Exits — Kanban + Analytics
          </h1>
        </div>
      </div>

      <AnalyticsBar analytics={analytics} />

      {analytics?.monthly_trend?.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-lg p-4" data-testid="exits-monthly-trend">
          <div className="flex items-center gap-2 mb-3">
            <LineChart className="h-4 w-4 text-slate-600" />
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
              Monthly Trend (last 12 months)
            </div>
          </div>
          <div className="flex items-end gap-2 h-24">
            {analytics.monthly_trend.map((m) => {
              const max = Math.max(...analytics.monthly_trend.map(x => x.count), 1);
              const h = Math.round(m.count / max * 100);
              return (
                <div key={m.month} className="flex-1 flex flex-col items-center gap-1">
                  <div className="text-[10px] font-semibold text-slate-600">{m.count || ""}</div>
                  <div
                    className="w-full bg-red-400 rounded-t"
                    style={{ height: `${Math.max(h, 6)}%` }}
                    title={`${m.month}: ${m.count} exits`}
                  />
                  <div className="text-[9px] text-slate-500 font-mono">{m.month.slice(5)}</div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {loading && <div className="text-sm text-slate-400">Loading…</div>}

      {!loading && totalCards === 0 && (
        <div className="border border-dashed border-slate-300 rounded-lg p-12 text-center text-sm text-slate-400">
          No exit records. When HR initiates a resignation or termination,
          the request will land here after Super Admin approval.
        </div>
      )}

      {!loading && totalCards > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="exits-kanban">
          {COL_ORDER.map((col) => {
            const items = kanban[col] || [];
            return (
              <div
                key={col}
                className={`bg-slate-50 rounded-lg border-t-4 ${COL_ACCENT[col]} p-3`}
                data-testid={`kanban-col-${col.toLowerCase().replace(/\s+/g,"-")}`}
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="text-[11px] uppercase tracking-wider text-slate-600 font-semibold">
                    {col}
                  </div>
                  <span className="text-[10px] bg-slate-200 text-slate-700 rounded-full px-2 py-0.5">
                    {items.length}
                  </span>
                </div>
                {items.length === 0 && (
                  <div className="text-[11px] text-slate-400 text-center py-6">—</div>
                )}
                {items.map((e) => <ExitCard key={e.id} e={e} />)}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
